import { X } from 'lucide-react';
import { FileMessage } from '@sendbird/chat/message';
import { useTranslate } from 'ra-core';
import { useFetchFileMessageCollection } from '../../api/hooks/sendbird/useFetchFileMessages';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '../../components/ui/dialog';
import { Spinner } from '../../components/admin/spinner';
import { fileMessagesToSlides } from './MediaViewer/fileMessagesToSlides';
import { MediaViewer } from './MediaViewer/MediaViewer';

type MediaViewerModalProps = {
  open: boolean;
  startingMessage: FileMessage;
  channelName: string;
  onClose: () => void;
};

export const MediaViewerModal = ({
  open,
  startingMessage,
  channelName,
  onClose,
}: MediaViewerModalProps) => {
  const translate = useTranslate();

  const {
    data: mediaMessageCollection,
    isLoading: isMediaMessageCollectionLoading,
    isError: isMediaMessageCollectionError,
  } = useFetchFileMessageCollection(startingMessage.createdAt);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogContent
        showCloseButton={false}
        className="flex h-screen max-h-screen w-screen max-w-none flex-col gap-0 rounded-none p-0 sm:max-w-none"
      >
        <DialogDescription className="sr-only">
          {translate('chat.mediaViewer.description')}
        </DialogDescription>
        <div className="flex h-full min-h-0 flex-col bg-background">
          <header className="flex h-14 shrink-0 items-center border-b bg-card px-4">
            <DialogTitle className="min-w-0 flex-1 truncate text-base leading-normal font-medium">
              {channelName}
            </DialogTitle>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={translate('chat.close')}
              onClick={onClose}
            >
              <X />
            </Button>
          </header>
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-hidden p-4">
            {isMediaMessageCollectionLoading && <Spinner size="large" />}

            {isMediaMessageCollectionError && (
              <p className="text-destructive">
                {translate('chat.mediaViewer.loadMoreMediaError')}
              </p>
            )}

            {mediaMessageCollection && (
              <MediaViewer
                startSlide={fileMessagesToSlides([startingMessage])[0]}
                messageCollection={mediaMessageCollection}
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
